import React, { useState, useEffect, useMemo } from "react";
import { TableColumnsType, Tag, notification } from "antd";
import { FinetuningService } from "../../services/FinetuningService";
import { usePermissions } from "../../PermissionsProvider";
import TableCard from "../shared/tablecard/TableCard";
import { Model } from "../../model_gen/model";
import TableDropDownMenu from "../shared/tablemenus/TableDropdownMenu";
import { PoweroffOutlined, LinkOutlined } from "@ant-design/icons";
import { ModelService } from "../../services/ModelService";
import { Observable } from "rxjs";
import { Table } from "antd";

const Models: React.FC = () => {
  const { apiKey } = usePermissions();
  const [modelData, setModelData] = useState<Model[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const fetchData = () => {
    FinetuningService.getFinetunedModels(setIsLoading, apiKey).subscribe(
      (data: Model[]) => {
        setModelData(data);
      },
      (error: Error) => {
        console.error("Error fetching model data:", error);
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [apiKey]);

  const groupedData = useMemo(() => {
    const groups = modelData.reduce(
      (acc: { [key: string]: Model[] }, model) => {
        if (!acc[model.model]) {
          acc[model.model] = [];
        }
        acc[model.model].push(model);
        return acc;
      },
      {}
    );

    return Object.entries(groups).map(([modelName, models]) => {
      // Ensure we have valid values for key components
      const groupId = models[0].intent_group_id || "no-group";

      return {
        key: `${modelName}-${groupId}`, // More stable composite key
        model: modelName,
        base_model: models.length === 1 ? models[0].base_model : undefined,
        group_name: models.length === 1 ? models[0].group_name : undefined,
        status: models.length === 1 ? models[0].status : undefined,
        date_added: models.length === 1 ? models[0].date_added : undefined,
        subModels: models.map((m) => ({
          ...m,
          key: m.id, // Should be unique
        })),
      };
    });
  }, [modelData]);

  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return groupedData.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, groupedData]);

  const columns: TableColumnsType<any> = [
    {
      title: "Model Name",
      dataIndex: "model",
      key: "model",
    },
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
    },
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string | undefined) => {
        if (!status) return "";

        let color = "default";
        switch (status.toLowerCase()) {
          case "active":
          case "enabled":
            color = "success";
            break;
          case "disabled":
            color = "error";
            break;
          case "pending":
            color = "processing";
            break;
          default:
            color = "default";
        }
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Date Created",
      dataIndex: "date_added",
      key: "date_added",
      render: (date: number | undefined) =>
        date ? new Date(date).toLocaleString() : "",
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => {
        if (record.subModels?.length > 1) return null;
        const model = record.subModels?.[0] || record;
        return (
          <TableDropDownMenu
            items={[
              {
                label: (
                  <a
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModelStatusToggle(model);
                    }}>
                    {model.status === "ENABLED" ? "Disable" : "Enable"}
                  </a>
                ),
                key: "0",
                icon: <PoweroffOutlined />,
              },
              {
                label: (
                  <a
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      // TODO: Implement finetune run link
                    }}>
                    View Finetune Run
                  </a>
                ),
                key: "1",
                icon: <LinkOutlined />,
                disabled: true,
              },
            ]}
          />
        );
      },
    },
  ];

  const expandedColumns: TableColumnsType<any> = [
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
    },
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "ENABLED" ? "success" : "error"}>{status}</Tag>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_added",
      key: "date_added",
      render: (date: number) => new Date(date).toLocaleString(),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <TableDropDownMenu
          items={[
            {
              label: (
                <a
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModelStatusToggle(record);
                  }}>
                  {record.status === "ENABLED" ? "Disable" : "Enable"}
                </a>
              ),
              key: "0",
              icon: <PoweroffOutlined />,
            },
            {
              label: (
                <a
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    // TODO: Implement finetune run link
                  }}>
                  View Finetune Run
                </a>
              ),
              key: "1",
              icon: <LinkOutlined />,
              disabled: true,
            },
          ]}
        />
      ),
    },
  ];

  const handleModelStatusToggle = (model: Model) => {
    const newStatus = model.status === "ENABLED" ? "DISABLED" : "ENABLED";
    setIsLoading(true);
    let modelObservable: Observable<any>;
    if (newStatus === "DISABLED") {
      modelObservable = ModelService.disableModel(
        model.id,
        setIsLoading,
        apiKey
      );
    } else {
      modelObservable = ModelService.enableModel(
        model.id,
        setIsLoading,
        apiKey
      );
    }
    modelObservable.subscribe(
      () => {
        fetchData();
        notification.success({
          message: "Success",
          description: `Model ${
            newStatus === "ENABLED" ? "enabled" : "disabled"
          } successfully`,
        });
      },
      (error: Error) => {
        console.error("Error updating model status:", error);
        notification.error({
          message: "Error",
          description: "Failed to update model status",
        });
      }
    );
  };

  return (
    <TableCard
      title="Models"
      columns={columns}
      dataSource={currentPageData}
      baseData={groupedData}
      isLoading={isLoading}
      currentPage_={currentPage}
      pageSize_={pageSize}
      pageTotal={groupedData.length}
      setCurrentPage={setCurrentPage}
      setPageSize={setPageSize}
      rowKey="key"
      expandableConfig={{
        expandedRowRender: (record) => (
          <div style={{ margin: 0, padding: "12px 48px" }}>
            <Table
              columns={expandedColumns}
              dataSource={record.subModels}
              pagination={false}
              rowKey="id"
            />
          </div>
        ),
        rowExpandable: (record) =>
          record?.subModels && record.subModels.length > 1,
      }}
    />
  );
};

export default Models;
